/**
 * StepFieldConfig：维度 / 指标 Tea Table 的列定义（含单元格编辑）。
 * 与 `useStepFieldConfigCore` 分工：此处仅组装 `TableColumn`，列编辑通过 `patchColumn` 写回父状态。
 */
import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { Checkbox, Input, Select, TableColumn, Text } from 'tea-component';

import type { TFieldTableCellKey } from '../../../utils/fieldConfigRowUtils';
import type { IYamlFieldColumn } from '../../../types';
import FieldConfigBatchSelectCheckbox from '../components/FieldConfigBatchSelectCheckbox';
import { FieldConfigRequiredLabel } from '../components/FieldConfigRequiredLabel';
import { coerceDimTypeSelectValue } from '../constants';
import { filtersText, sampleValuesText } from '../utils';
import type {
  IUseFieldConfigTableColumnsParams,
  IUseFieldConfigTableColumnsResult,
} from '../types';

/**
 * 构建 Step2 维度表、指标表的 Tea `Table` 列配置（含单元格内联编辑）。
 *
 * 必填列经 `renderTableCell` 包裹：点击「下一步」校验失败后对
 * `isTableCellError(source_column, fieldKey)` 为 true 的单元格加 error 边框；
 * `onChange` 内调用 `clearTableCellError` 清除单项高亮。
 *
 * @returns `dimColumns` — 仅维度行使用的列；`measColumns` — 仅指标行使用的列。
 */
export const useFieldConfigTableColumns = (
  p: IUseFieldConfigTableColumnsParams,
): IUseFieldConfigTableColumnsResult => {
  const {
    formatMessage,
    fieldsValue,
    changeFieldsValue,
    allDimSourceColumns,
    allMeasSourceColumns,
    patchColumn,
    handleFieldKindChange,
    kindFieldOptions,
    dimTypeOptions,
    measTypeOptions,
    measFormatOptions,
    measGranularityOptions,
    isTableCellError,
    clearTableCellError,
  } = p;

  /** 表格单元格：确认校验失败时包一层 error 边框 */
  const renderTableCell = useCallback(
    (
      col: IYamlFieldColumn,
      fieldKey: TFieldTableCellKey,
      node: React.ReactNode,
    ) => (
      <div
        className={classnames(
          'dbl-field-config-inline-add__field-input-wrap',
          {
            'dbl-field-config-inline-add__field-input-wrap--error':
              isTableCellError(col.source_column, fieldKey),
          },
        )}
      >
        {node}
      </div>
    ),
    [isTableCellError],
  );

  /** 列标题；`required` 为 true 时追加红色 * */
  const renderColHeader = useCallback(
    (msgId: string, required = false) => (
      <FieldConfigRequiredLabel required={required}>
        {formatMessage({ id: msgId })}
      </FieldConfigRequiredLabel>
    ),
    [formatMessage],
  );

  /** 表头全选：勾选态与 `batchKeys`∩`fieldsValue` 一致，点击对当前区域全部列全选/全取消 */
  const renderSelectAllHeader = useCallback(
    (batchKeys: string[]) => (
      <FieldConfigBatchSelectCheckbox
        variant="header"
        batchKeys={batchKeys}
        fieldsValue={fieldsValue}
        changeFieldsValue={changeFieldsValue}
        formatMessage={formatMessage}
      />
    ),
    [changeFieldsValue, fieldsValue, formatMessage],
  );

  /** 行内：参与 Cube 勾选 */
  const renderRowIncludeCheckbox = useCallback(
    (col: IYamlFieldColumn) => {
      const checked = fieldsValue.includes(col.source_column);
      return (
        <div className="dbl-field-config-row-select">
          <Checkbox
            value={checked}
            onChange={(nextChecked) => {
              const sc = col.source_column;
              let next: string[];
              if (!nextChecked) {
                next = fieldsValue.filter((s) => s !== sc);
              } else if (fieldsValue.includes(sc)) {
                next = fieldsValue;
              } else {
                next = [...fieldsValue, sc];
              }
              changeFieldsValue(next);
            }}
          />
        </div>
      );
    },
    [changeFieldsValue, fieldsValue],
  );

  /** 维度表：来源列、类型切换、展示名、SQL、维表关联等 */
  const dimColumns = useMemo(
    () =>
      [
        /** 表头：选择（参与 Cube 全选 / 行勾选） */
        {
          key: 'rowSelect',
          header: renderSelectAllHeader(allDimSourceColumns),
          width: 46,
          render: renderRowIncludeCheckbox,
        },
        /** 表头：字段名 */
        {
          key: 'name',
          header: formatMessage({ id: 'dbl_model_agent_step2_col_source' }),
          width: 200,
          render: (col: IYamlFieldColumn) => (
            <span>
              <Text theme="primary" className={`dbl-field-config-text ${col.is_custom ? 'dbl-field-config-text-custom' : ''}`}>
                {col.source_column}
              </Text>
              {col.is_custom ? (
                <Text className="dbl-field-config-custom-tag">
                  {formatMessage({ id: 'dbl_model_agent_step2_custom_tag' })}
                </Text>
              ) : null}
            </span>
          ),
        },
        /** 表头：类型 */
        {
          key: 'fieldKind',
          header: renderColHeader('dbl_model_agent_step2_col_kind', true),
          width: 130,
          render: (col: IYamlFieldColumn) => (
            <Select
              size="m"
              appearance="button"
              options={kindFieldOptions}
              value={col.default_kind}
              onChange={(v) => handleFieldKindChange(col.source_column, v)}
            />
          ),
        },
        /** 表头：展示名 */
        {
          key: 'displayName',
          header: renderColHeader('dbl_model_agent_step2_col_display', true),
          width: 180,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'displayName',
              <Input
                size="full"
                value={col.default_display_name}
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_col_display',
                })}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_display_name: v });
                  clearTableCellError(col.source_column, 'displayName');
                }}
              />,
            ),
        },
        /** 表头：中文名称 */
        {
          key: 'nameZh',
          header: renderColHeader('dbl_model_agent_step2_col_name_zh', true),
          width: 150,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'nameZh',
              <Input
                size="full"
                value={col.default_name_zh}
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_name_zh',
                })}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_name_zh: v });
                  clearTableCellError(col.source_column, 'nameZh');
                }}
              />,
            ),
        },
        /** 表头：英文名称 */
        {
          key: 'nameEn',
          header: renderColHeader('dbl_model_agent_step2_col_name_en', true),
          width: 150,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'nameEn',
              <Input
                size="full"
                value={col.default_name_en}
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_name_en',
                })}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_name_en: v });
                  clearTableCellError(col.source_column, 'nameEn');
                }}
              />,
            ),
        },
        /** 表头：数据类型 */
        {
          key: 'dataType',
          header: renderColHeader('dbl_model_agent_step2_col_data_type', true),
          width: 130,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'dataType',
              <Select
                size="m"
                appearance="button"
                options={dimTypeOptions}
                value={coerceDimTypeSelectValue(col.default_type)}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_type: String(v) });
                  clearTableCellError(col.source_column, 'dataType');
                }}
              />,
            ),
        },
        /** 表头：SQL */
        {
          key: 'sql',
          header: renderColHeader('dbl_model_agent_step2_col_sql', true),
          width: 260,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'sql',
              <Input
                size="full"
                value={col.default_sql}
                placeholder={formatMessage(
                  {
                    id: 'dbl_model_agent_step2_inline_sql_placeholder_dim',
                  },
                  { CUBE: '{CUBE}' },
                )}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_sql: v });
                  clearTableCellError(col.source_column, 'sql');
                }}
              />,
            ),
        },
        /** 表头：描述 */
        {
          key: 'description',
          header: renderColHeader('dbl_model_agent_step2_col_desc', true),
          width: 200,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'description',
              <Input
                size="full"
                value={col.default_description}
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_description',
                })}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_description: v });
                  clearTableCellError(col.source_column, 'description');
                }}
              />,
            ),
        },
        /** 表头：示例值 */
        {
          key: 'sample',
          header: renderColHeader('dbl_model_agent_step2_col_sample', true),
          width: 180,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'sampleValues',
              <Input
                size="full"
                value={sampleValuesText(col)}
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_sample_dim',
                })}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_sample_values: v });
                  clearTableCellError(col.source_column, 'sampleValues');
                }}
              />,
            ),
        },
        /** 表头：维度汇总值 */
        {
          key: 'dimSummary',
          header: formatMessage({ id: 'dbl_model_agent_step2_col_dim_summary' }),
          width: 140,
          render: (col: IYamlFieldColumn) => (
            <Input
              size="full"
              value={
                col.default_dim_summary_value !== null &&
                  col.default_dim_summary_value !== undefined
                  ? String(col.default_dim_summary_value)
                  : ''
              }
              placeholder={formatMessage({
                id: 'dbl_model_agent_step2_placeholder_dim_summary',
              })}
              onChange={(v) =>
                patchColumn(col.source_column, {
                  default_dim_summary_value: v,
                })
              }
            />
          ),
        },
        /** 表头：维度表 */
        {
          key: 'dimTable',
          header: formatMessage({ id: 'dbl_model_agent_step2_col_dim_table' }),
          width: 200,
          render: (col: IYamlFieldColumn) => (
            <Input
              size="full"
              value={col.default_dim_table || ''}
              placeholder={formatMessage({
                id: 'dbl_model_agent_step2_placeholder_dim_table',
              })}
              onChange={(v) =>
                patchColumn(col.source_column, { default_dim_table: v })
              }
            />
          ),
        },
        /** 表头：关联字段 */
        {
          key: 'dimJoinColumn',
          header: formatMessage({
            id: 'dbl_model_agent_step2_col_dim_join_column',
          }),
          width: 160,
          render: (col: IYamlFieldColumn) => (
            <Input
              size="full"
              value={col.default_dim_table_column || ''}
              onChange={(v) =>
                patchColumn(col.source_column, {
                  default_dim_table_column: v,
                })
              }
            />
          ),
        },
      ] as TableColumn<IYamlFieldColumn>[],
    [
      changeFieldsValue,
      allDimSourceColumns,
      dimTypeOptions,
      fieldsValue,
      formatMessage,
      handleFieldKindChange,
      kindFieldOptions,
      patchColumn,
      renderRowIncludeCheckbox,
      renderSelectAllHeader,
      renderColHeader,
      renderTableCell,
      clearTableCellError,
    ],
  );

  /** 指标表：含 `common_query`、格式、粒度、`filters` JSON 等 */
  const measColumns = useMemo(
    () =>
      [
        /** 表头：选择（参与 Cube 全选 / 行勾选） */
        {
          key: 'rowSelect',
          header: renderSelectAllHeader(allMeasSourceColumns),
          width: 46,
          render: renderRowIncludeCheckbox,
        },
        /** 表头：字段名 */
        {
          key: 'name',
          header: formatMessage({ id: 'dbl_model_agent_step2_col_source' }),
          width: 220,
          render: (col: IYamlFieldColumn) => (
            <span>
              <Text theme="primary" className={`dbl-field-config-text ${col.is_custom ? 'dbl-field-config-text-custom' : ''}`}>
                {col.source_column}
              </Text>
              {col.is_custom ? (
                <Text className="dbl-field-config-custom-tag">
                  {formatMessage({ id: 'dbl_model_agent_step2_custom_tag' })}
                </Text>
              ) : null}
            </span>
          ),
        },
        /** 表头：类型 */
        {
          key: 'fieldKind',
          header: renderColHeader('dbl_model_agent_step2_col_kind', true),
          width: 130,
          render: (col: IYamlFieldColumn) => (
            <Select
              size="m"
              appearance="button"
              options={kindFieldOptions}
              value={col.default_kind}
              onChange={(v) => handleFieldKindChange(col.source_column, v)}
            />
          ),
        },
        /** 表头：展示名 */
        {
          key: 'displayName',
          header: renderColHeader('dbl_model_agent_step2_col_display', true),
          width: 180,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'displayName',
              <Input
                size="full"
                value={col.default_display_name}
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_col_display',
                })}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_display_name: v });
                  clearTableCellError(col.source_column, 'displayName');
                }}
              />,
            ),
        },
        /** 表头：中文名称 */
        {
          key: 'nameZh',
          header: renderColHeader('dbl_model_agent_step2_col_name_zh', true),
          width: 150,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'nameZh',
              <Input
                size="full"
                value={col.default_name_zh}
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_name_zh',
                })}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_name_zh: v });
                  clearTableCellError(col.source_column, 'nameZh');
                }}
              />,
            ),
        },
        /** 表头：英文名称 */
        {
          key: 'nameEn',
          header: renderColHeader('dbl_model_agent_step2_col_name_en', true),
          width: 150,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'nameEn',
              <Input
                size="full"
                value={col.default_name_en}
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_name_en',
                })}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_name_en: v });
                  clearTableCellError(col.source_column, 'nameEn');
                }}
              />,
            ),
        },
        /** 表头：数据类型 */
        {
          key: 'dataType',
          header: renderColHeader('dbl_model_agent_step2_col_data_type', true),
          width: 150,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'dataType',
              <Select
                size="m"
                appearance="button"
                options={measTypeOptions}
                value={col.default_type}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_type: String(v) });
                  clearTableCellError(col.source_column, 'dataType');
                }}
              />,
            ),
        },
        /** 表头：常见查询问题 */
        {
          key: 'commonQuery',
          header: renderColHeader(
            'dbl_model_agent_step2_col_common_query',
            true,
          ),
          width: 220,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'commonQuery',
              <Input
                size="full"
                value={col.default_common_query || ''}
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_common_query',
                })}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_common_query: v });
                  clearTableCellError(col.source_column, 'commonQuery');
                }}
              />,
            ),
        },
        /** 表头：SQL */
        {
          key: 'sql',
          header: renderColHeader('dbl_model_agent_step2_col_sql', true),
          width: 260,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'sql',
              <Input
                size="full"
                value={col.default_sql}
                placeholder={formatMessage(
                  {
                    id: 'dbl_model_agent_step2_inline_sql_placeholder_meas',
                  },
                  { CUBE: '{CUBE}' },
                )}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_sql: v });
                  clearTableCellError(col.source_column, 'sql');
                }}
              />,
            ),
        },
        /** 表头：描述 */
        {
          key: 'description',
          header: renderColHeader('dbl_model_agent_step2_col_desc', true),
          width: 180,
          render: (col: IYamlFieldColumn) =>
            renderTableCell(
              col,
              'description',
              <Input
                size="full"
                value={col.default_description}
                placeholder={formatMessage({
                  id: 'dbl_model_agent_step2_placeholder_description',
                })}
                onChange={(v) => {
                  patchColumn(col.source_column, { default_description: v });
                  clearTableCellError(col.source_column, 'description');
                }}
              />,
            ),
        },
        /** 表头：格式 */
        {
          key: 'format',
          header: renderColHeader('dbl_model_agent_step2_col_format'),
          width: 120,
          render: (col: IYamlFieldColumn) => {
            const raw = col.default_format ?? '';
            const fv = measFormatOptions.some((o) => o.value === raw)
              ? raw
              : '';
            return (
              <Select
                size="m"
                appearance="button"
                options={measFormatOptions}
                value={fv}
                onChange={(v) =>
                  patchColumn(col.source_column, { default_format: String(v) })
                }
              />
            );
          },
        },
        /** 表头：数据粒度 */
        {
          key: 'granularity',
          header: formatMessage({ id: 'dbl_model_agent_step2_col_granularity' }),
          width: 120,
          render: (col: IYamlFieldColumn) => {
            const raw = col.default_granularity ?? '';
            const gv = measGranularityOptions.some((o) => o.value === raw)
              ? raw
              : '';
            return (
              <Select
                size="m"
                appearance="button"
                options={measGranularityOptions}
                value={gv}
                onChange={(v) =>
                  patchColumn(col.source_column, {
                    default_granularity: String(v),
                  })
                }
              />
            );
          },
        },
        /** 表头：筛选条件(JSON) */
        {
          key: 'filters',
          header: formatMessage({ id: 'dbl_model_agent_step2_col_filters' }),
          width: 200,
          render: (col: IYamlFieldColumn) => (
            <textarea
              className="dbl-field-config-filters-textarea"
              rows={1}
              value={filtersText(col)}
              placeholder={formatMessage({
                id: 'dbl_model_agent_step2_placeholder_filters',
              })}
              onChange={(e) =>
                patchColumn(col.source_column, {
                  filters_text: e.target.value,
                  filters: undefined,
                })
              }
            />
          ),
        },
      ] as TableColumn<IYamlFieldColumn>[],
    [
      changeFieldsValue,
      allMeasSourceColumns,
      fieldsValue,
      formatMessage,
      handleFieldKindChange,
      kindFieldOptions,
      measFormatOptions,
      measGranularityOptions,
      measTypeOptions,
      patchColumn,
      renderRowIncludeCheckbox,
      renderSelectAllHeader,
      renderColHeader,
      renderTableCell,
      clearTableCellError,
    ],
  );

  /** 供 `DimensionSection` / `MeasureSection` 传入 Tea `Table` 的 `columns` */
  return { dimColumns, measColumns };
};
